import { useCallback, useEffect, useState } from "react";
import { fetchReinvestmentPoolsByOwner } from "../services/api.js";

/**
 * Returns every Consolidated Reinvestment Pool (M4) owned by `address`,
 * plus a `refresh()` callback.
 *
 * Same data-flow contract as `useDeposits`: the frontend never reads the
 * chain directly for display — pools come from the backend indexer via
 * the API. Writes (createPool, attachDeposit, detachDeposit,
 * manualReinvest) still go wallet → contract, after which the caller
 * pings `/reinvestment/pools/:id/sync` and then `refresh()`.
 */
export function useReinvestmentPools(address) {
    const [pools, setPools] = useState([]);
    const [isLoading, setLoading] = useState(Boolean(address));
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        if (!address) {
            setPools([]);
            setLoading(false);
            return [];
        }
        setLoading(true);
        setError(null);
        try {
            const data = await fetchReinvestmentPoolsByOwner(address);
            const items = (data?.items || []).map(normaliseFromApi);
            setPools(items);
            // Return the fetched list so callers can wait for a specific
            // state (e.g. a newly-created pool appearing, or a member
            // landing in a pool's `members` array) without racing the
            // React state update.
            return items;
        } catch (err) {
            setError(err?.message || "Failed to load reinvestment pools from API");
            setPools([]);
            return [];
        } finally {
            setLoading(false);
        }
    }, [address]);

    useEffect(() => {
        if (address) {
            setLoading(true);
            setPools([]);
        }
        refresh();
    }, [address, refresh]);

    return { pools, isLoading, error, refresh };
}

function normaliseFromApi(raw) {
    return {
        poolId: Number(raw.poolId),
        owner: raw.owner,
        // §I.6 pool flavour. 0 = Standard, 1 = Turbo. Preserve BOTH the
        // numeric and label fields the backend ships so the UI can
        // discriminate without re-deriving from the label string.
        poolType: Number(raw.poolType ?? 0),
        poolTypeLabel: raw.poolTypeLabel || (Number(raw.poolType) === 1 ? "Turbo" : "Standard"),
        members: (raw.members || []).map(Number),
        memberCount: Number(raw.memberCount || 0),
        lifetimeMaterialized: raw.lifetimeMaterialized || "0",
        createdAt: Number(raw.createdAt || 0)
    };
}
